import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { goAfterLogin, postLoginTarget } from "@/features/auth/postLogin";
import { ApiError } from "@/lib/api";
import { ensureCsrf, useLogin } from "@/features/auth/hooks";
import { startLoginIntro } from "@/features/auth/intro";
import { AuthLayout } from "./AuthLayout";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/field";

export function LoginPage() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    ensureCsrf();
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const res = await login.mutateAsync({ username: username.trim().toLowerCase(), password });
      const target = postLoginTarget(location);
      if (res.status === "2fa_required") navigate("/login/2fa", { replace: true, state: { from: target } });
      else {
        // Kirish muvaffaqiyatli: FATH logo animatsiyasi, platforma uning ostida ochiladi.
        // 2FA'ni avval sozlash kerak bo'lsa -- intro sozlash tugagach (TwoFactorSetupPage)
        if (!res.user?.must_setup_2fa) startLoginIntro();
        goAfterLogin(target, navigate);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("login.failed"));
    }
  }

  return (
    <AuthLayout title={t("login.title")} footer={t("login.footer")}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && (
          <div role="alert" className="rounded-[10px] border border-danger bg-danger-soft px-3 py-2 text-[12px] text-danger">
            {error}
          </div>
        )}
        <Field label={t("login.username")} htmlFor="username">
          <Input id="username" type="text" autoComplete="username" autoCapitalize="none" spellCheck={false} autoFocus value={username} onChange={(e) => setUsername(e.target.value)} placeholder={t("login.usernamePlaceholder")} required />
        </Field>
        <Field label={t("login.password")} htmlFor="password">
          <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </Field>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={login.isPending}>
          {t("login.submit")}
        </Button>
      </form>
    </AuthLayout>
  );
}
